import { useEffect, useRef, useState } from 'react'

import { logDebug } from '@core/logging'
import { getLockManager } from '@core/polyfills'

/**
 * Requests to acquire a Web Lock with the given name.
 * Note: If shouldRequestLock changes to false after the lock is granted, the
 * lock will NOT be automatically released. To release the lock, you need to
 * explicitly call the release function returned by this hook.
 *
 * @returns A function to release the Web Lock
 */
export function useWebLock(lockName: string, shouldRequestLock: boolean) {
  const [releaseLock, setReleaseLock] = useState<() => void>()
  const lockAcquiredRef = useRef(false)

  useEffect(() => {
    if (shouldRequestLock && !releaseLock) {
      const controller = new AbortController()

      getLockManager().then((lockManager) => {
        lockAcquiredRef.current = false
        const lockOptions = { signal: controller.signal }

        logDebug(`Requesting lock: ${lockName}`)
        lockManager
          .request(lockName, lockOptions, () => {
            logDebug(`Lock acquired: ${lockName}`)
            lockAcquiredRef.current = true
            const promise = new Promise<void>((resolve) => {
              setReleaseLock(() => {
                return () => {
                  resolve()
                  logDebug(`Lock released: ${lockName}`)
                }
              })
            })
            return promise
          })
          .then(() => {
            setReleaseLock(undefined)
          })
          .catch(() => {
            // When the lock request is aborted because it is no longer needed an
            // error gets thrown in this catch block, but there is no need to
            // handle or log it.
          })
      })

      return () => {
        // If the lock was requested but never acquired, abort the request.
        if (!lockAcquiredRef.current) {
          controller.abort('lock aborted')
        }
      }
    }
  }, [lockName, releaseLock, shouldRequestLock])

  return releaseLock
}
